import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import {
  applyPwaUpdate,
  isIOS,
  isMobileDevice,
  isStandalone,
  onPwaState,
  promptInstall,
} from '../lib/pwa';

const DISMISS_KEY = 'di-install-dismissed-at';
// Re-prompt after this many days if the user dismissed without installing.
const REPROMPT_AFTER_DAYS = 14;

function recentlyDismissed() {
  try {
    const ts = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < REPROMPT_AFTER_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function recordDismiss() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode etc — silent */
  }
}

// Two banners stacked into one component:
//   • Update banner — an updated service worker is waiting; one tap reloads.
//   • Install banner — the browser fired beforeinstallprompt and the user is
//     a logged-in mobile visitor. iOS shows manual instructions instead.
export default function InstallPrompt() {
  const { user } = useAuth();
  const [state, setState] = useState({
    canInstall: false,
    needsRefresh: false,
    offlineReady: false,
  });
  const [iosHintOpen, setIosHintOpen] = useState(false);

  useEffect(() => onPwaState(setState), []);

  const mobile = isMobileDevice();
  const standalone = isStandalone();
  const ios = isIOS();

  // Update banner takes priority — even installed users should refresh.
  if (state.needsRefresh) {
    return (
      <div
        className="position-fixed start-50 translate-middle-x px-3 py-2 d-flex align-items-center gap-3"
        style={{
          bottom: 16,
          zIndex: 1080,
          background: '#0f172a',
          color: '#fff',
          borderRadius: 999,
          boxShadow: '0 12px 32px rgba(15,23,42,0.35)',
          maxWidth: 'calc(100vw - 24px)',
          fontSize: '0.9rem',
        }}
      >
        <i className="fas fa-cloud-download-alt" style={{ color: '#a5f3fc' }}></i>
        <span>New version available.</span>
        <button
          type="button"
          className="btn btn-sm btn-light fw-semibold"
          onClick={applyPwaUpdate}
        >
          Refresh
        </button>
      </div>
    );
  }

  // Don't pester non-mobile or non-logged-in users.
  if (!user) return null;
  if (!mobile) return null;
  if (standalone) return null;
  if (recentlyDismissed()) return null;

  // Android / Edge / Chromium on mobile — native prompt available.
  if (state.canInstall) {
    return (
      <Banner
        icon="fa-mobile-alt"
        title="Install Dissertation Island"
        body="Save it to your home screen — works offline, opens fast, no browser bar."
        primaryLabel="Install"
        onPrimary={async () => {
          const { outcome } = await promptInstall();
          if (outcome !== 'accepted') recordDismiss();
        }}
        onDismiss={() => {
          recordDismiss();
          // Force re-render so the banner disappears immediately.
          setState((s) => ({ ...s }));
        }}
      />
    );
  }

  // iOS Safari — no native prompt. Show a "how to add to home screen" hint
  // on demand. Only render the small bar; expand on tap.
  if (ios) {
    if (iosHintOpen) {
      return (
        <Banner
          icon="fa-apple"
          title="Add to Home Screen"
          body={
            <span>
              Tap <i className="fas fa-share" style={{ margin: '0 4px' }}></i>
              <strong>Share</strong>, then choose{' '}
              <strong>Add to Home Screen</strong>.
            </span>
          }
          primaryLabel="Got it"
          onPrimary={() => {
            setIosHintOpen(false);
            recordDismiss();
          }}
          onDismiss={() => {
            setIosHintOpen(false);
            recordDismiss();
          }}
        />
      );
    }
    return (
      <Banner
        icon="fa-mobile-alt"
        title="Install on this iPhone"
        body="Add to your home screen for one-tap access."
        primaryLabel="How"
        onPrimary={() => setIosHintOpen(true)}
        onDismiss={() => {
          recordDismiss();
          setState((s) => ({ ...s }));
        }}
      />
    );
  }

  return null;
}

function Banner({ icon, title, body, primaryLabel, onPrimary, onDismiss }) {
  return (
    <div
      className="position-fixed start-50 translate-middle-x p-3"
      style={{
        bottom: 16,
        zIndex: 1080,
        background: '#fff',
        color: 'var(--ink)',
        borderRadius: 14,
        boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
        maxWidth: 'calc(100vw - 24px)',
        width: 380,
        border: '1px solid rgba(15,23,42,0.06)',
      }}
    >
      <div className="d-flex align-items-start gap-3">
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
            color: '#fff',
            display: 'inline-grid',
            placeItems: 'center',
            flexShrink: 0,
            fontSize: '1rem',
          }}
        >
          <i className={`fas ${icon}`}></i>
        </span>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold">{title}</div>
          <div className="small text-muted" style={{ lineHeight: 1.4 }}>
            {body}
          </div>
          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-cta"
              onClick={onPrimary}
            >
              {primaryLabel}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link text-muted px-2"
              onClick={onDismiss}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
